import React, { useEffect, useState, useRef } from 'react';

const HelpCenterDetail = () => {
  const [activeTab, setActiveTab] = useState('support-tab-1');
  const sectionRefs = useRef({});

  const sections = [
    { id: 'support-tab-1', title: 'Summary of key points' },
    { id: 'support-tab-2', title: 'What information do we collect ?' },
    { id: 'support-tab-3', title: 'How do we process your information ?' },
    { id: 'support-tab-4', title: 'When and with whom do we share your personal information ?' },
    { id: 'support-tab-5', title: 'Do we use cookies and other tracking technologies ?' },
    { id: 'support-tab-6', title: 'How do we handle your social logins ?' },
    { id: 'support-tab-7', title: 'How long do we keep your information ?' },
    { id: 'support-tab-8', title: 'How do we keep your information safe ?' },
    { id: 'support-tab-9', title: 'Do we collect information from minors ?' },
    { id: 'support-tab-10', title: 'What are your privacy rights ?' },
    { id: 'support-tab-11', title: 'Controls for do-not-track features' },
    { id: 'support-tab-12', title: 'Do we make updates to this policy ?' },
    { id: 'support-tab-13', title: 'How can you review, update, or delete the data we collect from you ?' },
    { id: 'support-tab-14', title: 'How can you contact us ?' },
  ];

  // Observe sections on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveTab(entry.target.id);
          }
        });
      },
      { threshold: 0.5 }
    );

    sections.forEach((section) => {
      if (sectionRefs.current[section.id]) {
        observer.observe(sectionRefs.current[section.id]);
      }
    });

    return () => {
      sections.forEach((section) => {
        if (sectionRefs.current[section.id]) {
          observer.unobserve(sectionRefs.current[section.id]);
        }
      });
    };
  }, []);

  // Smooth scroll on click
  const handleClick = (id) => {
    const element = sectionRefs.current[id];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section className="support-content pb-60">
      <div className="container">
        <div className="row justify-content-between">
          {/* Sidebar */}
          <div className="col-lg-4 col-md-4 d-none d-md-block d-lg-block">
            <div className="support-article-sidebar sticky-sidebar">
              <div className="nav flex-column nav-pills support-article-tab bg-light rounded-custom p-4">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    className={`nav-link text-start fw-bold ${
                      activeTab === section.id ? 'active' : ''
                    }`}
                    onClick={() => handleClick(section.id)}
                  >
                    {section.title}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-lg-7 col-md-8">
            <div>
              {/* Section 1 */}
              <div
                id="support-tab-1"
                ref={(el) => (sectionRefs.current['support-tab-1'] = el)}
                style={{ scrollMarginTop: '80px' }}
                className="support-article-wrap mb-5"
              >
                <h4 className='mb-4'>Summary of key points </h4>
                    <p>This summary provides key points from our privacy policy, but you can find out more details about any of these topics by clicking the link following each key point or by using our table of contents below to find the section you are looking for.</p>
                    <ul className='support-article-list list-unstyled mt-4'>
                      <li className='py-4 border-top border-light'>
                        <h6 className=' support-article-title'>
                          What personal information do we process?
                        </h6>
                        <p>
                          When you visit, use, or navigate our services, we may process personal information depending on how you interact with us and the services, the choices you make, and the products and features you use. Learn more about the personal information you disclose to us.
                        </p>
                      </li>
                      <li className='py-4 border-top border-light'>
                        <h6 className='support-article-title'>
                          Do we process any sensitive personal information?
                        </h6>
                        <p>
                          Some of the information may be considered “special” or “sensitive” in certain jurisdictions, for example, your racial or ethnic origins, sexual orientation, and religious beliefs. We do not process sensitive personal information.
                        </p>

                      </li>
                      <li className='py-4 border-top border-light'>
                        <h6 className='support-article-title'>
                          Do we collect any information from third parties?
                        </h6>
                        <p>
                          We do not collect any information from third parties.
                        </p>
                      </li>
                      <li className='py-4 border-top border-light'>
                        <h6 className='support-article-title'>
                          How do we process your information?
                        </h6>
                        <p>
                          We process your information to provide, improve, and administer our services, communicate with you, for security and fraud prevention, and to comply with the law. We may also process your information for other purposes with your consent. We process your information only when we have a valid legal reason to do so. Learn more about how we process your information.
                        </p>
                      </li>
                      <li className='py-4 border-top border-light'>
                        <h6 className=' support-article-title'>
                          In what situations and with which parties do we share personal information?
                        </h6>
                        <p>
                          We may share information in specific situations and with specific third parties. Learn more about when and with whom we share your personal information.

                        </p>
                      </li>
                      <li className='py-4 border-top border-light'>
                        <h6 className=' support-article-title'>
                          How do we keep your information safe?
                        </h6>
                        <p>
                          We have adequate organisational and technical processes and procedures in place to protect your personal information. However, no electronic transmission over the internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorised third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information. Learn more about how we keep your information safe.
                        </p>
                      </li>
                      <li className='py-4 border-top border-light'>
                        <h6 className=' support-article-title'>
                          What are your rights?
                        </h6>
                        <p>
                          Depending on where you are located geographically, the applicable privacy law may mean you have certain rights regarding your personal information. Learn more about your privacy rights.
                        </p>
                      </li>
                      <li className='py-4 border-top border-light'>
                        <h6 className='support-article-title'>
                          How do you exercise your rights?
                        </h6>
                        <p>
                          The easiest way to exercise your rights is by submitting a data subject access request or by contacting us. We will consider and act upon any request in accordance with applicable data protection laws.

                          Want to learn more about what we do with any information we collect? review the privacy policy in full.

                        </p>
                      </li>
                    </ul>
              </div>

              {/* Section 2 */}
              <div
                id="support-tab-2"
                ref={(el) => (sectionRefs.current['support-tab-2'] = el)}
                style={{ scrollMarginTop: '80px' }}
                className="support-article-wrap mb-5"
              >
                 <h4 className='mb-4'>What information do we collect ?</h4>
                    <p>We collect personal information that you voluntarily provide to us when you register on the services, express an interest in obtaining information about us or our products and/or services, when you participate in activities on the services, or otherwise when you contact us.</p>
                    <ul className='support-article-list list-unstyled mt-4'>
                      <li className='py-4 border-top border-light'>
                        <h6 className=' support-article-title'>
                          Personal information provided by you
                        </h6>
                        <p>
                          The personal information that we collect depends on the context of your interactions with us and the services, the choices you make, and the products and features you use. The personal information we collect may include the following :-<br />

                          1. Name<br />
                          2. Phone numbers<br />
                          3. Email addresses<br />
                          4. Billing addresses

                        </p>
                      </li>
                      <li className='py-4 border-top border-light'>
                        <h6 className='support-article-title'>
                          Third party login data
                        </h6>
                        <p>
                          We may provide you with the option to register with us using third-party service providers such as Google, Microsoft, Apple and/or your existing social media account details, like your Facebook, X, or other social media account. If you choose to register in this way, we will collect certain profile information about you from the service provider, as described in the section called how do we handle your social logins? below. All personal information that you provide to us must be true, complete, and accurate, and you must notify us of any changes to such personal information.

                        </p>

                      </li>
                      <li className='py-4 border-top border-light'>

                        <h6 className='support-article-title'>
                          Information automatically collected
                        </h6>
                        <p>
                          {' '}
                          We automatically collect certain information when you visit, use, or navigate the services. This information does not reveal your specific identity (like your name or contact information) but may include device and usage information, such as your IP address, browser and device characteristics, operating system, language preferences, referring URLs, device name, country, location, information about how and when you use our services, and other technical information. This information is primarily needed to maintain the security and operation of our services, and for our internal analytics and reporting purposes.<br />
<br/>
                          Like many businesses, we also collect information through cookies and similar technologies. The information we collect includes service-related logs and usage data, diagnostic, usage, and performance information our servers automatically collect when you access or use our services and which we record in log files. Depending on how you interact with us, this log data may include your IP address, device information, browser type, and settings and information about your activity in the services (such as the date/time stamps associated with your usage, pages and files viewed, searches, and other actions you take such as which features you use), device event information (such as system activity, error reports (sometimes called “crash dumps”), and hardware settings).
                        </p>
                      </li>
                    </ul>
              </div>

              {/* Section 3 */}
              <div
                id="support-tab-3"
                ref={(el) => (sectionRefs.current['support-tab-3'] = el)}
                style={{ scrollMarginTop: '80px' }}
                className="support-article-wrap mb-5"
              >
                <h4 className='mb-4'>How do we process your information ?</h4>

                    
                      <p>
  We process your information to provide, improve, and administer our services, 
  communicate with you, for security and fraud prevention, and to comply with the law. 
  We may also process your information for other purposes with your consent. 
  We process your personal information for a variety of reasons, depending on how you 
  interact with our services, including :-
</p>

<ol>
  <li>
    To facilitate account creation and authentication and otherwise manage user accounts. 
    We may process your information so you can create and log in to your account, as well 
    as keep your account in working order.
  </li>
  <li>
    To deliver and facilitate the delivery of services to the user. We may process your 
    information to provide you with the requested service.
  </li>
  <li>
    To respond to user inquiries/offer support to users. We may process your information 
    to respond to your inquiries and solve any potential issues you might have with the 
    requested service.
  </li>
  <li>
    To send you marketing and promotional communications. We may process the personal 
    information you send to us for our marketing purposes, if this is in accordance with 
    your marketing preferences.
  </li>
  <li>
    You can opt out of our marketing emails at any time. For more information, see what 
    your privacy rights are below.
  </li>
  <li>
    To deliver targeted advertising to you. We may process your information to develop and 
    display personalised content and advertising tailored to your interests, location, and more.
  </li>
  <li>
    To evaluate and improve our services, products, marketing, and your experience. We may 
    process your information when we believe it is necessary to identify usage trends, 
    determine the effectiveness of our promotional campaigns, and evaluate and improve 
    our services, products, marketing, and your experience.
  </li>
  <li>
    To identify usage trends. We may process information about how you use our services to 
    better understand how they are being used so we can improve them.
  </li>
  <li>
    To determine the effectiveness of our marketing and promotional campaigns. We may 
    process your information to better understand how to provide marketing and promotional 
    campaigns that are most relevant to you.
  </li>
  <li>
    To comply with our legal obligations. We may process your information to comply with 
    our legal obligations, respond to legal requests, and exercise, establish, or defend 
    our legal rights.
  </li>
</ol>
              </div>

               {/* Section 4 */}
              <div
                id="support-tab-4"
                ref={(el) => (sectionRefs.current['support-tab-4'] = el)}
                style={{ scrollMarginTop: '80px' }}
                className="support-article-wrap mb-5"
              >
                  <h4 className='mb-4'>When and with whom do we share your personal information ?</h4>

                    <p>
                      We may share information in specific situations described in this section and/or with the following third parties. We may need to share your personal information in the following situations :-<br />
                                       <br/>
 <ol><li><span className='fw-bold'>Business Transfers</span>: We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.
</li></ol>

                    </p>
              </div>


               {/* Section 5 */}
              <div
                id="support-tab-5"
                ref={(el) => (sectionRefs.current['support-tab-5'] = el)}
                style={{ scrollMarginTop: '80px' }}
                className="support-article-wrap mb-5"
              >
                <h4 className='mb-4'> Do we use cookies and other tracking technologies ?</h4>


                    <p>
                      We may use cookies and similar tracking technologies (like web beacons and pixels) to gather information when you interact with our services. Some online tracking technologies help us maintain the security of our services and your account, prevent crashes, fix bugs, save your preferences, and assist with basic site functions.<br /><br />

                      We also permit third parties and service providers to use online tracking technologies on our services for analytics and advertising, including to help manage and display advertisements, to tailor advertisements to your interests, or to send abandoned shopping cart reminders (depending on your communication preferences).<br /><br />

                      The third parties and service providers use their technology to provide advertising about products and services tailored to your interests, which may appear either on our services or on other websites. Specific information about how we use such technologies and how you can refuse certain cookies is set out in our Cookie Policy.<br /><br />

                      We may share your information with Google Analytics to track and analyse the use of the services. The Google Analytics advertising features that we may use include remarketing with Google Analytics, Google Display Network impressions reporting, and Google Analytics demographics and interests reporting. To opt out of being tracked by Google Analytics across the services, visit https://tools.google.com/dlpage/gaoptout. You can opt out of Google Analytics advertising features through the ad settings and ad settings for mobile apps. Other opt-out means include http://optout.networkadvertising.org/ and http://www.networkadvertising.org/ mobile choice. For more information on the privacy practices of Google, please visit the Google privacy & terms page.

                    </p>
                  
              </div>


               {/* Section 6 */}
              <div
                id="support-tab-6"
                ref={(el) => (sectionRefs.current['support-tab-6'] = el)}
                style={{ scrollMarginTop: '80px' }}
                className="support-article-wrap mb-5"
              >
                <h4 className='mb-4'>
                      How do we handle your social logins ?
                    </h4>
                    <ul className='support-article-list list-unstyled'>
                      <p>
                        If you choose to register or log in to our services using a social media account, we may have access to certain information about you. Our services offer you the ability to register and log in using your third-party service provider account details (like your Google, Microsoft, Apple, Meta or X logins). Where you choose to do this, we will receive certain profile information about you from your service provider. The profile information we receive may vary depending on the service provider concerned, but will often include your name, email address, friends list, and profile picture, as well as other information you choose to make public on such a platform.<br /><br />
                        We will use the information we receive only for the purposes that are described in this privacy policy or that are otherwise made clear to you on the relevant services. Please note that we do not control, and are not responsible for, other uses of your personal information by your third-party social media provider. We recommend that you review their privacy policy to understand how they collect, use, and share your personal information, and how you can set your privacy preferences on their sites and apps.
                      </p>
                    </ul>
                  
              </div>

               {/* Section 7 */}
              <div
                id="support-tab-7"
                ref={(el) => (sectionRefs.current['support-tab-7'] = el)}
                style={{ scrollMarginTop: '80px' }}
                className="support-article-wrap mb-5"
              >
                <h4 className='mb-4'> 
                     How long do we keep your information ?
                    </h4>
                    <ul className='support-article-list list-unstyled'>
                      <p>
                        We will only keep your personal information for as long as it is necessary for the purposes set out in this privacy policy, unless a longer retention period is required or permitted by law (such as tax, accounting, or other legal requirements). No purpose in this policy will require us to keep your personal information for longer than the period during which users have an account with us.<br /><br />

                        When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymise such information, or, if this is not possible (for example, because your personal information has been stored in backup archives), then we will securely store your personal information and isolate it from any further processing until deletion is possible.

                      </p>
                    </ul>
                 
              </div>

              
               {/* Section 8 */}
              <div
                id="support-tab-8"
                ref={(el) => (sectionRefs.current['support-tab-8'] = el)}
                style={{ scrollMarginTop: '80px' }}
                className="support-article-wrap mb-5"
              >
                  <h4 className='mb-4'>
                      How do we keep your information safe ?
                    </h4>
                    <ul className='support-article-list list-unstyled'>
                      <p>
                        We have implemented appropriate and reasonable technical and organisational security measures designed to protect the security of any personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorised third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information. Although we will do our best to protect your personal information, transmission of personal information to and from our services is at your own risk. You should only access the services within a secure environment.
                      </p>
                    </ul>
                  
              </div>


               {/* Section 9 */}
              <div
                id="support-tab-9"
                ref={(el) => (sectionRefs.current['support-tab-9'] = el)}
                style={{ scrollMarginTop: '80px' }}
                className="support-article-wrap mb-5"
              >
                <h4 className='mb-4'>
                      Do we collect information from minors ?
                    </h4>
                    <ul className='support-article-list list-unstyled'>
                      <p>
                        We do not knowingly collect, solicit data from, or market to children under 18 years of age, nor do we knowingly sell such personal information. By using the services, you represent that you are at least 18 or that you are the parent or guardian of such a minor and consent to such minor's use of the services. If we learn that personal information from users less than 18 years of age has been collected, we will deactivate the account and take reasonable measures to promptly delete such data from our records. If you become aware of any data we may have collected from children under age 18, please contact us, contact details provided in the section how can you contact us? below.
                      </p>
                    </ul>
                  
              </div>


               {/* Section 10 */}
              <div
                id="support-tab-10"
                ref={(el) => (sectionRefs.current['support-tab-10'] = el)}
                style={{ scrollMarginTop: '80px' }}
                className="support-article-wrap mb-5"
              >
                 <h4>
                      What are your privacy rights?
                    </h4>
                    <ul className='support-article-list list-unstyled mt-4'>
                      <li className='py-4 border-top border-light'>
                        <h6 className=' support-article-title'>
                          Withdrawing your consent
                        </h6>
                        <p>
                          If we are relying on your consent to process your personal information, which may be express and/or implied consent depending on the applicable law, you have the right to withdraw your consent at any time. You can withdraw your consent at any time by contacting us using the contact details provided in the section how can you contact us? below.<br /><br />
                          However, please note that this will not affect the lawfulness of the processing before its withdrawal, nor, when applicable law allows, will it affect the processing of your personal information conducted in reliance on lawful processing grounds other than consent.

                        </p>
                      </li>
                      <li className='py-4 border-top border-light'>
                        <h6 className='support-article-title'>
                          Opting out of marketing and promotional communications
                        </h6>
                        <p>
                          You can unsubscribe from our marketing and promotional communications at any time by replying “STOP” or “UNSUBSCRIBE” to the messages that we send, or by contacting us using the contact details provided in the section how can you contact us? below. You will then be removed from the marketing lists. However, we may still communicate with you, for example, to send you service-related messages that are necessary for the administration and use of your account, to respond to service requests, or for other non-marketing purposes.
                        </p>
                      </li>
                      <li className='py-4 border-top border-light'>
                        <h6 className='support-article-title'>
                          Account Information
                        </h6>
                        <p>
                          If you would at any time like to review or change the information in your account or terminate your account, you can log in to your account settings and update your user account.<br /><br />

                          Upon your request to terminate your account, we will deactivate or delete your account and information from our active databases. However, we may retain some information in our files to prevent fraud, troubleshoot problems, assist with any investigations, enforce our legal terms and/or comply with applicable legal requirements.

                        </p>
                      </li>
                      <li className='py-4 border-top border-light'>
                        <h6 className='support-article-title'>
                          Cookies and similar technologies
                        </h6>
                        <p>
                          Most web browsers are set to accept cookies by default. If you prefer, you can usually choose to set your browser to remove cookies and to reject cookies. If you choose to remove cookies or reject cookies, this could affect certain features or services of our services.<br /><br />

                          If you have questions or comments about your privacy rights, you may contact us, contact details provided in the section how can you contact us? below.

                        </p>
                      </li>

                    </ul>
                 
              </div>


               {/* Section 11 */}
              <div
                id="support-tab-11"
                ref={(el) => (sectionRefs.current['support-tab-11'] = el)}
                style={{ scrollMarginTop: '80px' }}
                className="support-article-wrap mb-5"
              >
                <h4 className='mb-4'>
                      Controls for do-not-track features
                    </h4>
                    <p>
                      Most web browsers and some mobile operating systems and mobile applications include a do-not-track feature or setting you can activate to signal your privacy preference not to have data about your online browsing activities monitored and collected. At this stage, no uniform technology standard for recognising and implementing do-not-track signals has been finalised. As such, we do not currently respond to do-not-track browser signals or any other mechanism that automatically communicates your choice not to be tracked online. If a standard for online tracking is adopted that we must follow in the future, we will inform you about that practice in a revised version of this privacy policy.

                    </p>
                  
              </div>


               {/* Section 12 */}
              <div
                id="support-tab-12"
                ref={(el) => (sectionRefs.current['support-tab-12'] = el)}
                style={{ scrollMarginTop: '80px' }}
                className="support-article-wrap mb-5"
              >
                <h4 className='mb-4'>
                      Do we make updates to this policy ?
                    </h4>
                    <p>
                      We may update this privacy policy from time to time. The updated version will be indicated by an updated “revised” date at the bottom of this privacy policy. If we make material changes to this privacy policy, we may notify you either by prominently posting a notice of such changes or by directly sending you a notification. We encourage you to review this privacy policy frequently to be informed of how we are protecting your information.



                    </p>
                  
              </div>


               {/* Section 13 */}
              <div
                id="support-tab-13"
                ref={(el) => (sectionRefs.current['support-tab-13'] = el)}
                style={{ scrollMarginTop: '80px' }}
                className="support-article-wrap mb-5"
              >
                 <h4 className='mb-4'>
                      How can you review, update, or delete the data we collect from you ?

                    </h4>
                    <p>
                      Based on the applicable laws of your country, you may have the right to request access to the personal information we collect from you, details about how we have processed it, correct inaccuracies, or delete your personal information. You may also have the right to withdraw your consent to our processing of your personal information. These rights may be limited in some circumstances by applicable law. To request any of these activities, you can connect with us, contact details provided in the section how can you 
                      contact us? below.



                    </p>
                 
              </div>



              {/* Section 14 */}
              <div
                id="support-tab-14"
                ref={(el) => (sectionRefs.current['support-tab-14'] = el)}
                style={{ scrollMarginTop: '80px' }}
                className="support-article-wrap mb-5"
              >
                <h4>
                      How can you contact us ?
                    </h4>
                    <p>
                      If you have questions or comments about this privacy policy, you may email us at hello@senertech.in.
                    </p>
                
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HelpCenterDetail;

